import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, Lightbulb, X, ZoomIn, GraduationCap, MessageCircle, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chaptersFor } from "@/lib/tutorial";
import { Button, Page, PageHeader } from "@/components/kit";

const key = (uid) => `tutorial-read-${uid}`;
const readSet = (uid) => { try { return new Set(JSON.parse(localStorage.getItem(key(uid)) || "[]")); } catch { return new Set(); } };

function Shot({ step, onZoom, testid }) {
  return (
    <button type="button" onClick={onZoom} className="relative block w-full rounded-2xl overflow-hidden border group" style={{ borderColor: "var(--border)", background: "var(--surface-alt)" }} data-testid={testid}>
      <img src={step.img} alt={step.title} loading="lazy" className="w-full h-auto block" />
      {step.spot && (
        <span className="absolute pointer-events-none" style={{ left: `${step.spot[0]}%`, top: `${step.spot[1]}%`, transform: "translate(-50%,-50%)" }}>
          <span className="block w-10 h-10 rounded-full animate-ping absolute inset-0" style={{ background: "rgba(214,120,60,0.45)" }} />
          <span className="block w-10 h-10 rounded-full border-[3px] relative" style={{ borderColor: "#D6783C", background: "rgba(214,120,60,0.15)" }} />
        </span>
      )}
      <span className="absolute right-3 top-3 rounded-full px-2.5 py-1 text-xs font-semibold flex items-center gap-1 opacity-90 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,0.65)", color: "#fff" }}><ZoomIn size={13} /> Ampliar</span>
    </button>
  );
}

function Zoom({ step, onClose }) {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  if (!step) return null;
  return (
    <div className="fixed inset-0 z-50 overflow-auto flex lg:items-center lg:justify-center p-4" style={{ background: "rgba(0,0,0,0.85)" }} onClick={onClose} data-testid="tutorial-zoom">
      <button className="fixed top-4 right-4 z-10 text-white rounded-full p-1" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose} data-testid="tutorial-zoom-close"><X size={26} /></button>
      <img src={step.img} alt={step.title} className="rounded-xl max-w-none w-[1280px] lg:w-auto lg:max-w-full lg:max-h-full m-auto" />
    </div>
  );
}

function Step({ step, n, onZoom }) {
  return (
    <div className="grid lg:grid-cols-[1fr_1.35fr] gap-5 lg:gap-8 items-start fade-in" data-testid={`tutorial-step-${n}`}>
      <div className="flex gap-4">
        <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-bold text-white tnum" style={{ background: "var(--brand)" }}>{n}</span>
        <div className="flex flex-col gap-2">
          <h3 className="h3">{step.title}</h3>
          <p className="text-sm sm:text-base leading-relaxed" style={{ color: "var(--text-2, var(--text))" }}>{step.text}</p>
          {step.tip && <p className="text-sm rounded-xl p-3 flex gap-2" style={{ background: "var(--brand-tint)" }}><Lightbulb size={16} className="shrink-0 mt-0.5" style={{ color: "var(--brand)" }} />{step.tip}</p>}
        </div>
      </div>
      <Shot step={step} onZoom={onZoom} testid={`tutorial-step-image-${n}`} />
    </div>
  );
}

export const usePlatform = () => useQuery({ queryKey: ["platform"], queryFn: () => api("/public/platform"), staleTime: 300000 });

function ChapterVideo({ ch }) {
  const { data } = usePlatform();
  const custom = data?.videos?.[ch.id];
  const src = custom ? `${process.env.REACT_APP_BACKEND_URL}${custom}` : `/tutorial/v-${ch.id}.mp4`;
  return (
    <div className="rounded-3xl overflow-hidden mb-4" style={{ background: "#111" }}>
      <video key={src} src={src} poster={ch.cover} controls playsInline preload="metadata" className="w-full aspect-video block" data-testid="tutorial-chapter-video">
        {!custom && <track kind="subtitles" srcLang="pt-BR" label="Português" src={`/tutorial/v-${ch.id}.vtt`} default data-testid="tutorial-chapter-captions" />}
      </video>
    </div>
  );
}

function SupportButton({ ch }) {
  const { data } = usePlatform();
  const { user } = useAuth();
  if (!data?.support_whatsapp) return null;
  const text = `Olá! Estou com dúvida no tutorial, capítulo "${ch.title}".${user?.tenant?.name ? ` Restaurante: ${user.tenant.name}.` : ""}`;
  return (
    <div className="rounded-2xl p-5 mt-12 flex flex-wrap items-center gap-4" style={{ background: "#E8F6EC" }} data-testid="tutorial-support-card">
      <MessageCircle size={28} style={{ color: "#1F8A4C" }} />
      <div className="flex-1 min-w-[200px]"><div className="label text-base">Ainda com dúvida?</div><p className="caption">Fale com o nosso suporte pelo WhatsApp. A mensagem já vai com o nome deste capítulo.</p></div>
      <a href={`https://wa.me/${data.support_whatsapp}?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" className="btn" style={{ background: "#25D366", color: "#fff" }} data-testid="tutorial-support-whatsapp">
        <MessageCircle size={16} /> Chamar no WhatsApp
      </a>
    </div>
  );
}

function ChapterView({ chapters, ch, markRead }) {
  const nav = useNavigate();
  const [zoom, setZoom] = useState(null);
  const i = chapters.findIndex((c) => c.id === ch.id);
  const next = chapters[i + 1];
  useEffect(() => { window.scrollTo({ top: 0 }); }, [ch.id]);
  const finish = () => { markRead(ch.id); nav(next ? `/tutorial/${next.id}` : "/tutorial"); };
  return (
    <Page>
      <PageHeader title={ch.title} subtitle={`Capítulo ${i + 1} de ${chapters.length} · ${ch.time}`} back="/tutorial" />
      <ChapterVideo ch={ch} />
      <div className="flex items-start gap-3 mb-10">
        <PlayCircle size={20} className="shrink-0 mt-0.5" style={{ color: "var(--brand)" }} />
        <p className="text-sm sm:text-base max-w-3xl" data-testid="tutorial-chapter-intro">{ch.intro} <span className="caption">Assista ao vídeo e depois siga os passos abaixo.</span></p>
      </div>
      <div className="flex flex-col gap-12">{ch.steps.map((st, k) => <Step key={st.title} step={st} n={k + 1} onZoom={() => setZoom(st)} />)}</div>
      <SupportButton ch={ch} />
      <div className="flex flex-wrap items-center justify-between gap-3 mt-8 pt-6 border-t" style={{ borderColor: "var(--border)" }}>
        {i > 0 ? <Button variant="ghost" icon={ArrowLeft} onClick={() => nav(`/tutorial/${chapters[i - 1].id}`)} data-testid="tutorial-prev-chapter"><span className="hidden sm:inline">{chapters[i - 1].title}</span><span className="sm:hidden">Anterior</span></Button> : <span />}
        <Button icon={next ? ArrowRight : CheckCircle2} onClick={finish} className="max-w-full whitespace-normal h-auto py-3 text-left" data-testid="tutorial-next-chapter">{next ? `Concluir e ir para: ${next.title}` : "Concluir tutorial"}</Button>
      </div>
      <Zoom step={zoom} onClose={() => setZoom(null)} />
    </Page>
  );
}

function ChapterCard({ c, n, done }) {
  return (
    <Link to={`/tutorial/${c.id}`} className="group rounded-3xl overflow-hidden border flex flex-col transition-transform duration-200 hover:-translate-y-1" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid={`tutorial-chapter-${c.id}`}>
      <div className="relative overflow-hidden">
        <img src={c.cover} alt="" loading="lazy" className="w-full h-36 object-cover transition-transform duration-500 group-hover:scale-105" />
        {done && <span className="absolute top-3 right-3 rounded-full px-2.5 py-1 text-xs font-semibold flex items-center gap-1" style={{ background: "var(--success)", color: "#fff" }} data-testid={`tutorial-chapter-done-${c.id}`}><CheckCircle2 size={13} /> Visto</span>}
      </div>
      <div className="p-4 flex flex-col gap-1.5 flex-1">
        <div className="caption tnum flex items-center gap-2">Capítulo {n} · <Clock size={12} /> {c.time} · {c.steps.length} passos</div>
        <div className="label text-base">{c.title}</div>
        <p className="caption">{c.intro}</p>
      </div>
    </Link>
  );
}

export default function Tutorial() {
  const { id } = useParams();
  const auth = useAuth();
  const chapters = useMemo(() => chaptersFor(auth), [auth]);
  const uid = auth.user?.id;
  const [read, setRead] = useState(() => readSet(uid));
  const markRead = (cid) => { const s2 = new Set(read).add(cid); setRead(s2); localStorage.setItem(key(uid), JSON.stringify([...s2])); };
  const ch = chapters.find((c) => c.id === id);
  if (ch) return <ChapterView chapters={chapters} ch={ch} markRead={markRead} />;
  const doneCount = chapters.filter((c) => read.has(c.id)).length;
  const first = chapters.find((c) => !read.has(c.id)) || chapters[0];
  return (
    <Page>
      <PageHeader title="Tutorial" subtitle="Aprenda a usar o sistema passo a passo, no seu ritmo" back="/menu" />
      <div className="rounded-3xl p-6 sm:p-8 mb-8 flex flex-wrap items-center gap-6" style={{ background: "var(--brand)", color: "#fff" }} data-testid="tutorial-hero">
        <GraduationCap size={40} />
        <div className="flex-1 min-w-[220px]">
          <div className="text-xl sm:text-2xl font-bold">{doneCount ? `Você já viu ${doneCount} de ${chapters.length} capítulos` : "Comece por aqui"}</div>
          <p className="text-sm opacity-90 mt-1">Cada capítulo tem passos curtos com a tela do sistema e o ponto exato onde tocar.</p>
          <div className="h-2 rounded-full mt-3 overflow-hidden" style={{ background: "rgba(255,255,255,0.25)" }}><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${(100 * doneCount) / chapters.length}%`, background: "#fff" }} data-testid="tutorial-progress-bar" /></div>
        </div>
        <Link to={`/tutorial/${first.id}`} className="btn" style={{ background: "#fff", color: "var(--brand)" }} data-testid="tutorial-start-button">{doneCount ? "Continuar" : "Começar"} <ArrowRight size={16} /></Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tutorial-chapter-list">{chapters.map((c, k) => <ChapterCard key={c.id} c={c} n={k + 1} done={read.has(c.id)} />)}</div>
    </Page>
  );
}
